'use client';
import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import { Toaster } from 'sonner';

type Mode = 'login' | 'signup' | 'reset';

export default function AuthScreen() {
  const [mode, setMode] = useState<Mode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [ranchName, setRanchName] = useState('');
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);
  const { signIn, signUp, resetPassword } = useAuth();
  const router = useRouter();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === 'login') {
        await signIn(email, password);
        router.push('/dashboard');
      } else if (mode === 'signup') {
        await signUp(email, password, { fullName, ranchName });
        toast.success('Account created! Welcome to CattleBooks.');
        router.push('/dashboard');
      } else if (mode === 'reset') {
        await resetPassword(email);
        toast.success('Password reset email sent. Check your inbox.');
        setMode('login');
      }
    } catch (err: any) {
      toast.error(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex">
      <Toaster position="top-center" />

      {/* Left Brand Panel */}
      <div
        className="hidden lg:flex lg:w-[420px] flex-col justify-between p-12"
        style={{ background: 'linear-gradient(160deg, #2D5016 0%, #1e3a0f 100%)' }}
      >
        <div>
          <p className="font-bold text-2xl text-white">CattleBooks</p>
          <p className="text-green-300 text-sm mt-1">Simple cattle records</p>
        </div>
        <div>
          <h2 className="text-3xl font-bold text-white leading-tight mb-4">
            The digital ranch notebook.
          </h2>
          <p className="text-green-200 text-base leading-relaxed mb-8">
            Track cows, calves, breeding, treatments, and herd history without complicated software.
          </p>
          <ul className="space-y-3 text-green-100 text-sm">
            <li>Track every cow, calf, and bull</li>
            <li>Log breeding, preg checks, and AI records</li>
            <li>Record weights with growth history</li>
            <li>Health treatments and withdrawal dates</li>
            <li>Dam-sire lineage on every animal</li>
          </ul>
        </div>
        <div className="bg-white/10 rounded-xl p-5">
          <p className="text-white text-sm font-semibold mb-1">14-day free trial</p>
          <p className="text-green-200 text-sm">Then just $5 per month. Cancel anytime.</p>
        </div>
      </div>

      {/* Right Form Panel */}
      <div className="flex-1 flex items-center justify-center p-6 bg-white">
        <div className="w-full max-w-sm">
          {/* Mobile Brand */}
          <div className="lg:hidden text-center mb-8">
            <p className="font-bold text-2xl text-gray-900">CattleBooks</p>
            <p className="text-sm text-gray-500 mt-1">Simple cattle records</p>
          </div>

          {/* Mode Tabs */}
          {mode !== 'reset' && (
            <div className="flex bg-gray-100 rounded-lg p-1 mb-8">
              <button
                onClick={() => setMode('login')}
                className={`flex-1 py-2.5 text-sm font-semibold rounded-md transition-all ${
                  mode === 'login' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Sign In
              </button>
              <button
                onClick={() => setMode('signup')}
                className={`flex-1 py-2.5 text-sm font-semibold rounded-md transition-all ${
                  mode === 'signup' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Create Account
              </button>
            </div>
          )}

          {mode === 'reset' && (
            <div className="mb-8">
              <h2 className="text-xl font-bold text-gray-900">Reset Password</h2>
              <p className="text-sm text-gray-500 mt-1">Enter your email to receive a reset link.</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'signup' && (
              <>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Ranch Name</label>
                  <input
                    type="text"
                    value={ranchName}
                    onChange={(e) => setRanchName(e.target.value)}
                    placeholder="Circle R Ranch"
                    className="w-full px-4 py-3.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-green-700 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Your Name</label>
                  <input
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="Wade Johnson"
                    className="w-full px-4 py-3.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-green-700 focus:border-transparent"
                  />
                </div>
              </>
            )}

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@ranch.com"
                required
                className="w-full px-4 py-3.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-green-700 focus:border-transparent"
              />
            </div>

            {mode !== 'reset' && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={6}
                  className="w-full px-4 py-3.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-green-700 focus:border-transparent"
                />
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 bg-green-800 text-white font-semibold rounded-xl hover:bg-green-900 transition-colors disabled:opacity-60 text-sm mt-2"
            >
              {loading
                ? 'Please wait...'
                : mode === 'login' ?'Sign In'
                : mode === 'signup' ?'Create Account — Start Free Trial' :'Send Reset Link'}
            </button>
          </form>

          <div className="mt-5 text-center space-y-2">
            {mode === 'login' && (
              <button
                onClick={() => setMode('reset')}
                className="text-sm text-gray-500 hover:text-gray-700 underline"
              >
                Forgot password?
              </button>
            )}
            {mode === 'reset' && (
              <button
                onClick={() => setMode('login')}
                className="text-sm text-gray-500 hover:text-gray-700 underline"
              >
                Back to Sign In
              </button>
            )}
            {mode === 'signup' && (
              <p className="text-xs text-gray-500">
                14-day free trial. Then $5/month. Cancel anytime.
              </p>
            )}
          </div>

          {/* Demo credentials */}
          <div className="mt-6 p-4 bg-gray-50 rounded-xl border border-gray-200">
            <p className="text-xs font-semibold text-gray-600 mb-1">Demo Account</p>
            <p className="text-xs text-gray-500">demo@cattlebooks.com</p>
            <p className="text-xs text-gray-500">ranchlife2026</p>
          </div>
        </div>
      </div>
    </div>
  );
}